// Panel de cuentas de Claude en la oficina (22-10-2026, diseño final aprobado por Pau: docs/cuentas-claude/). Un
// cartel más en la pared, junto a Cerebros y Calypso, que dice cuántas cuentas tienen margen; al pulsarlo, una línea
// por cuenta. En el móvil, una línea bajo la cabecera del equipo que abre la lista a pantalla entera.
// Qué se enseña en cada situación lo decide modelo.ts; aquí solo se pinta.
import { WIDGET } from '../ui';
import * as Dialogo from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { startRunnersUsagePolling, useRunnersUsageStore } from '../../../stores/runners-usage.store';
import { useOficinaStore } from '../store';
import { VentanaReconexion } from './VentanaReconexion';
import { cuando, filasDeCuentas, hace, resumen, tono, type FilaCuenta, type Resumen, type Tramo } from './modelo';

/** La sala donde se contrata a quien arregla una cuenta: la de la propia oficina. */
const SALA_OFICINA = 'dashboard';

const PUNTO: Record<Resumen['estado'], string> = {
  margen: 'bg-oficina-trabajando',
  justo: 'bg-oficina-bloqueado',
  parado: 'bg-oficina-error',
  sin: 'bg-oficina-esperando',
};
const RELLENO = { normal: 'bg-oficina-texto2', cerca: 'bg-oficina-bloqueado', agotada: 'bg-oficina-error' } as const;
const CIFRA = { normal: 'text-oficina-texto', cerca: 'text-oficina-bloqueado', agotada: 'text-oficina-error' } as const;

function useCuentas() {
  const entradas = useRunnersUsageStore((s) => s.entriesByRunnerId);
  const cargado = useRunnersUsageStore((s) => s.loaded);
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const parar = startRunnersUsagePolling();
    return parar;
  }, []);
  useEffect(() => {
    const id = window.setInterval(() => setAhora(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);
  return useMemo(() => {
    const lista = Object.values(entradas);
    const filas = filasDeCuentas(lista, ahora);
    return { cargado, filas, res: resumen(filas, lista), ahora };
  }, [entradas, cargado, ahora]);
}

function Barra({ pct }: { pct: number | null }) {
  if (pct === null) return <div className="h-1 rounded-full border border-dashed border-oficina-linea" />;
  return (
    <div className="relative h-1 overflow-hidden rounded-full bg-oficina-linea">
      {pct > 0 ? <div className={cn('absolute inset-y-0 left-0 min-w-[3px] rounded-full', RELLENO[tono(pct)])} style={{ width: `${Math.min(100, pct)}%` }} /> : null}
    </div>
  );
}

function cabeceraPlaca(res: Resumen, ahora: number): string | null {
  if (res.estado === 'sin') return t('cuentasClaude.sinLecturaHace', { hace: hace(res.leidoMs, ahora) });
  if (res.vuelveMs !== null) return t(res.estado === 'parado' ? 'cuentasClaude.unaEn' : 'cuentasClaude.otraEn', { cuando: cuando(res.vuelveMs, ahora) });
  if (res.sinLectura) return t('cuentasClaude.sinLectura', { n: res.sinLectura });
  return null;
}

/** El cartel de la pared (escritorio). Misma caja que Cerebros y Calypso. */
export function PlacaCuentas() {
  const { cargado, filas, res, ahora } = useCuentas();
  const [abierta, setAbierta] = useState(false);
  const [reconectando, setReconectando] = useState<FilaCuenta | null>(null);
  useReconectarDesdeEnlace(filas, setReconectando);
  if (!cargado || !filas.length) return null;
  const nota = cabeceraPlaca(res, ahora);
  return (
    <>
    {reconectando ? <VentanaReconexion runnerId={reconectando.runnerId} nombre={reconectando.nombre} alCerrar={() => setReconectando(null)} /> : null}
    <Popover.Root open={abierta} onOpenChange={setAbierta}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={t('cuentasClaude.abrir', { libres: res.libres, leidas: res.leidas })}
          className={WIDGET}
          data-testid="placa-cuentas"
        >
          <span className="flex items-center gap-1.5 truncate text-o-mini text-oficina-texto2">
            <span className={cn('h-2 w-2 shrink-0 rounded-full', PUNTO[res.estado])} />
            {t('cuentasClaude.placa')}
            {nota ? <span className="truncate text-oficina-texto3">· {nota}</span> : null}
          </span>
          <span className="mt-1 block whitespace-nowrap tabular-nums">
            <span className="text-o-titulo2 font-bold leading-7 text-oficina-texto">{res.estado === 'sin' ? '—' : t('cuentasClaude.deCuantas', { libres: res.libres, leidas: res.leidas })}</span>{' '}
            {res.estado !== 'sin' ? <span className="text-o-sub font-medium text-oficina-texto2">{t('cuentasClaude.libres')}</span> : null}
          </span>
          <span className="mt-1.5 flex gap-[3px]" aria-hidden>
            {filas.map((f) => (
              <span key={f.runnerId} className="flex-1">
                <Barra pct={f.cinco || f.semana ? Math.max(f.cinco?.pct ?? 0, f.semana?.pct ?? 0) : null} />
              </span>
            ))}
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="z-40 w-[480px] rounded-o-m border border-oficina-linea bg-oficina-panel px-[18px] pb-2 pt-4 text-oficina-texto shadow-o-hoja outline-none animate-in fade-in-0 zoom-in-95"
          data-testid="detalle-cuentas"
        >
          <Detalle
            filas={filas}
            res={res}
            ahora={ahora}
            alArreglar={() => setAbierta(false)}
            alConectar={(f) => {
              setAbierta(false);
              setReconectando(f);
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
    </>
  );
}

/** `?reconectar=<cuenta>` en la dirección abre «Volver a conectar» para esa cuenta aunque no esté caducada (prueba
 *  con Pau, o reconectar a propósito). Solo titular: el servidor lo niega a cualquier otro. */
function useReconectarDesdeEnlace(filas: FilaCuenta[], abrir: (f: FilaCuenta) => void) {
  const [hecho, setHecho] = useState(false);
  useEffect(() => {
    if (hecho || !filas.length) return;
    const nombre = new URLSearchParams(window.location.search).get('reconectar');
    if (!nombre) return;
    const f = filas.find((x) => x.nombre === nombre);
    if (f) abrir(f);
    setHecho(true);
  }, [filas, hecho, abrir]);
}

/** Lo que hay en «Arréglalo»: abre «Contratar» en la sala de la oficina con el problema ya escrito. */
function useArreglar(alHecho: () => void) {
  const abrirContratar = useOficinaStore((s) => s.abrirContratar);
  return (f: FilaCuenta) => {
    const problema = f.situacion === 'caducada' ? t('cuentasClaude.caducada') : f.motivo ? t(`cuentasClaude.motivo.${f.motivo}`) : t('cuentasClaude.leyendo');
    abrirContratar(SALA_OFICINA, t('cuentasClaude.encargo', { nombre: f.nombre, runner: f.runnerId, min: f.sinLecturaMin, problema }));
    alHecho();
  };
}

function Cifra({ tramo, extra, ahora }: { tramo: Tramo | null; extra?: string; ahora: number }) {
  if (!tramo) return <div />;
  const tn = tono(tramo.pct);
  const vuelve = [cuando(tramo.vuelveMs, ahora), extra].filter(Boolean).join(' · ');
  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex items-baseline justify-between gap-2 text-o-mini tabular-nums">
        <b className={cn('text-o-sub font-semibold', CIFRA[tn])}>{tn === 'agotada' ? t('cuentasClaude.agotada') : `${tramo.pct} %`}</b>
        <span className="truncate text-oficina-texto3">{vuelve}</span>
      </div>
      <Barra pct={tramo.pct} />
    </div>
  );
}

/** Notas discretas bajo una cuenta leída. Solo el pago por uso va en naranja (es dinero). */
function notaDeFila(f: FilaCuenta, ahora: number): { texto: string; aviso: boolean } | null {
  const partes: string[] = [];
  if (f.pagoPorUso) {
    const fmt = (v: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: f.pagoPorUso?.moneda ?? 'EUR' }).format(v);
    partes.push(t('cuentasClaude.pagoPorUso', { gastado: fmt(f.pagoPorUso.gastado), limite: f.pagoPorUso.limite !== null ? fmt(f.pagoPorUso.limite) : '—' }));
  }
  if (f.planMenor) partes.push(t('cuentasClaude.planMenor', { plan: f.planMenor }));
  if (f.leidoMs !== null && ahora - f.leidoMs > 3 * 60_000) partes.push(t('cuentasClaude.leidoHace', { hace: hace(f.leidoMs, ahora) }));
  return partes.length ? { texto: partes.join(' · '), aviso: (f.pagoPorUso?.gastado ?? 0) > 0 } : null;
}

function FilaProblema({ f, alArreglar, alConectar, puedeArreglar }: { f: FilaCuenta; alArreglar: (f: FilaCuenta) => void; alConectar: (f: FilaCuenta) => void; puedeArreglar: boolean }) {
  const titulo =
    f.situacion === 'impagada' ? t('cuentasClaude.impagada')
    : f.situacion === 'caducada' ? t('cuentasClaude.caducada')
    : f.sinLecturaMin >= 1 ? t('cuentasClaude.sinLecturaDesde', { min: f.sinLecturaMin }) : t('cuentasClaude.leyendo');
  const nota =
    f.situacion === 'impagada' ? t('cuentasClaude.impagadaNota', { nombre: f.nombre })
    : f.situacion === 'caducada' ? t('cuentasClaude.caducadaNota') : f.motivo ? t(`cuentasClaude.motivo.${f.motivo}`) : null;
  const rojo = f.situacion === 'caducada' || f.situacion === 'impagada';
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-o-sub font-semibold">
        <span className={cn('h-2 w-2 shrink-0 rounded-full', rojo ? 'bg-oficina-error' : 'bg-oficina-esperando')} />
        {titulo}
      </div>
      {nota ? <p className="mt-1 text-o-mini leading-snug text-oficina-texto3">{nota}</p> : null}
      {f.boton && puedeArreglar ? (
        <button
          type="button"
          onClick={() => (f.boton === 'conectar' ? alConectar(f) : alArreglar(f))}
          className="mt-2 inline-flex h-8 items-center rounded-o-s bg-oficina-acento px-3 text-o-mini font-semibold text-oficina-acento-tinta transition-colors hover:bg-oficina-acento-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
          data-testid={`arreglar-${f.runnerId}`}
        >
          {f.boton === 'conectar' ? t('cuentasClaude.reconexion.boton') : t('cuentasClaude.arreglar')}
        </button>
      ) : null}
    </div>
  );
}

const REJILLA = 'grid grid-cols-[112px_1fr_1fr] gap-5';

function Detalle({ filas, res, ahora, alArreglar, alConectar }: { filas: FilaCuenta[]; res: Resumen; ahora: number; alArreglar: () => void; alConectar: (f: FilaCuenta) => void }) {
  const arreglar = useArreglar(alArreglar);
  const yo = useOficinaStore((s) => s.yo);
  const puedeArreglar = !yo || yo.rol === 'titular';
  return (
    <>
      <div className="mb-3 flex items-baseline justify-between">
        <span className="text-o-cuerpo font-semibold">{t('cuentasClaude.titulo')}</span>
        <span className="text-o-mini text-oficina-texto3">{hace(res.leidoMs, ahora)}</span>
      </div>
      <div className={cn(REJILLA, 'pb-1.5 text-o-mini text-oficina-texto3')}>
        <span />
        <span>{t('cuentasClaude.cinco')}</span>
        <span>{t('cuentasClaude.semana')}</span>
      </div>
      {filas.map((f) => {
        const leida = !!(f.cinco || f.semana);
        const nota = leida ? notaDeFila(f, ahora) : null;
        return (
          <div key={f.runnerId} className={cn(REJILLA, 'items-center border-t border-oficina-linea py-[11px]')} data-testid={`cuenta-${f.runnerId}`}>
            <div className="truncate text-o-sub font-medium">{f.nombre}</div>
            {leida ? (
              <>
                <Cifra tramo={f.cinco} ahora={ahora} />
                <Cifra tramo={f.semana} ahora={ahora} extra={f.fable ? t('cuentasClaude.fable', { pct: f.fable.pct }) : undefined} />
                {nota ? <p className={cn('col-start-2 col-end-4 -mt-2 text-o-mini', nota.aviso ? 'text-oficina-bloqueado' : 'text-oficina-texto3')}>{nota.texto}</p> : null}
              </>
            ) : (
              <div className="col-span-2">
                <FilaProblema f={f} alArreglar={arreglar} alConectar={alConectar} puedeArreglar={puedeArreglar} />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

/** Móvil: una línea bajo la cabecera del equipo; al tocarla, la lista a pantalla entera. */
export function LineaCuentasMovil() {
  const { cargado, filas, res, ahora } = useCuentas();
  const [abierta, setAbierta] = useState(false);
  const [reconectando, setReconectando] = useState<FilaCuenta | null>(null);
  useReconectarDesdeEnlace(filas, setReconectando);
  if (!cargado || !filas.length) return null;
  return (
    <>
    {reconectando ? <VentanaReconexion runnerId={reconectando.runnerId} nombre={reconectando.nombre} alCerrar={() => setReconectando(null)} /> : null}
    <Dialogo.Root open={abierta} onOpenChange={setAbierta}>
      <Dialogo.Trigger asChild>
        <button
          type="button"
          aria-label={t('cuentasClaude.abrir', { libres: res.libres, leidas: res.leidas })}
          className="inline-flex h-11 items-center gap-2 rounded-full px-1 text-o-sub focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
          data-testid="linea-cuentas"
        >
          <span className="inline-flex h-[30px] items-center gap-2 rounded-full border border-oficina-linea bg-oficina-elevada px-3">
            <span className={cn('h-2 w-2 rounded-full', PUNTO[res.estado])} />
            <span className="text-oficina-texto2">{t('cuentasClaude.placa')}</span>
            <b className="whitespace-nowrap font-semibold tabular-nums text-oficina-texto">{res.estado === 'sin' ? '—' : t('cuentasClaude.deCuantas', { libres: res.libres, leidas: res.leidas })}</b>
            <ChevronRight className="h-3.5 w-3.5 text-oficina-texto3" aria-hidden />
          </span>
        </button>
      </Dialogo.Trigger>
      <Dialogo.Portal>
        <Dialogo.Content className="fixed inset-0 z-50 overflow-y-auto bg-oficina-panel px-4 pb-[calc(theme(spacing.4)+env(safe-area-inset-bottom))] pt-[calc(theme(spacing.3)+env(safe-area-inset-top))] text-oficina-texto outline-none" data-testid="detalle-cuentas-movil">
          <Dialogo.Close asChild>
            <button type="button" className="-ml-2 inline-flex h-11 items-center gap-1 rounded-o-s px-2 text-o-cuerpo text-oficina-texto2 focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70">
              <ChevronLeft className="h-4 w-4" aria-hidden />
              {t('cuentasClaude.volver')}
            </button>
          </Dialogo.Close>
          <div className="mb-3.5 mt-0.5 flex items-baseline justify-between">
            <Dialogo.Title className="text-o-titulo2 font-semibold">{t('cuentasClaude.titulo')}</Dialogo.Title>
            <span className="text-o-mini text-oficina-texto3">{hace(res.leidoMs, ahora)}</span>
          </div>
          <Dialogo.Description className="sr-only">{t('cuentasClaude.abrir', { libres: res.libres, leidas: res.leidas })}</Dialogo.Description>
          <ListaMovil
            filas={filas}
            ahora={ahora}
            alArreglar={() => setAbierta(false)}
            alConectar={(f) => {
              setAbierta(false);
              setReconectando(f);
            }}
          />
        </Dialogo.Content>
      </Dialogo.Portal>
    </Dialogo.Root>
    </>
  );
}

function ListaMovil({ filas, ahora, alArreglar, alConectar }: { filas: FilaCuenta[]; ahora: number; alArreglar: () => void; alConectar: (f: FilaCuenta) => void }) {
  const arreglar = useArreglar(alArreglar);
  const yo = useOficinaStore((s) => s.yo);
  const puedeArreglar = !yo || yo.rol === 'titular';
  const linea = (etq: string, tramo: Tramo | null, extra?: string) => {
    if (!tramo) return null;
    const tn = tono(tramo.pct);
    const vuelve = [cuando(tramo.vuelveMs, ahora), extra].filter(Boolean).join(' · ');
    return (
      <div className="mt-2.5">
        <div className="mb-1.5 flex items-baseline justify-between text-o-sub tabular-nums">
          <span className="text-oficina-texto3">{etq}</span>
          <span>
            <b className={cn('font-semibold', CIFRA[tn])}>{tn === 'agotada' ? t('cuentasClaude.agotada') : `${tramo.pct} %`}</b>
            {vuelve ? <span className="text-oficina-texto3"> · {vuelve}</span> : null}
          </span>
        </div>
        <Barra pct={tramo.pct} />
      </div>
    );
  };
  return (
    <>
      {filas.map((f) => {
        const leida = !!(f.cinco || f.semana);
        const nota = leida ? notaDeFila(f, ahora) : null;
        return (
          <div key={f.runnerId} className="border-t border-oficina-linea py-3.5">
            <div className="text-o-cuerpo font-semibold">{f.nombre}</div>
            {leida ? (
              <>
                {linea(t('cuentasClaude.cinco'), f.cinco)}
                {linea(t('cuentasClaude.semana'), f.semana, f.fable ? t('cuentasClaude.fable', { pct: f.fable.pct }) : undefined)}
                {nota ? <p className={cn('mt-2 text-o-mini', nota.aviso ? 'text-oficina-bloqueado' : 'text-oficina-texto3')}>{nota.texto}</p> : null}
              </>
            ) : (
              <div className="mt-2">
                <FilaProblema f={f} alArreglar={arreglar} alConectar={alConectar} puedeArreglar={puedeArreglar} />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
